import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface SessionUser {
    name: string;
    role_name: string;
    role_id: number;
    uuid: string;
    username: string;
    company_id: number;
    company_name: string;
    email_signature: string | null;
}

export interface SessionOwner {
    userId: string;
    signature?: string;
    companyId?: number;
    companyName?: string;
    name?: string;
    role?: string;
    roleId?: string;
}

const LS_OWNER_KEY = 'sessionOwner';
const CHANNEL_NAME = 'auth';
const DISBURSEMENT_FLAG = 'disbursement_mode';

@Injectable({ providedIn: 'root' })
export class AuthSessionService implements OnDestroy {

    private sessionValid$ = new BehaviorSubject<boolean | null>(null);
    /** Stream of session validity. null = checking, true = active, false = none */
    sessionValid = this.sessionValid$.asObservable();

    private userProfile$ = new BehaviorSubject<SessionUser | null>(null);
    /** Stream of the current user profile (Name, Role, etc.) */
    userProfile = this.userProfile$.asObservable();

    private authChannel: BroadcastChannel | null = null;
    private checkPromise: Promise<boolean> | null = null;
    private pendingInitPromise: Promise<boolean> | null = null;

    /** Throttling for tab-focus validation (60-second cooldown) */
    private lastCheckTime: number = 0;
    private visibilityListener: (() => void) | null = null;

    private storageListener: ((event: StorageEvent) => void) | null = null;
    constructor(private http: HttpClient, private router: Router) {
        this.initBroadcastChannel();
        this.initStorageListener();
        this.initVisibilityListener();
        this.refreshProfileFromStorage();
    }

    /**
     * Extracts Name and Role from storage to initialize the reactive stream.
     */
    public refreshProfileFromStorage(): void {
        const user = {
            name: sessionStorage.getItem('name') || '',
            role_name: sessionStorage.getItem('role') || '',
            role_id: Number(sessionStorage.getItem('roleId')) || 0,
            uuid: sessionStorage.getItem('uuid') || '',
            username: sessionStorage.getItem('username') || '',
            company_id: Number(sessionStorage.getItem('companyId')) || 0,
            company_name: sessionStorage.getItem('companyName') || '',
            email_signature: sessionStorage.getItem('signature')
        } as SessionUser;

        if (user.username || user.uuid || user.name) {
            this.userProfile$.next(user);
        } else {
            this.userProfile$.next(null);
        }
    }

    private hasToken(): boolean {
        return !!sessionStorage.getItem('username');
    }

    // ─── Browser-Level Session Lock (localStorage) ──────────────────────────

    /**
     * Checks if a different user already owns the session in this browser.
     */
    isSessionLocked(incomingUserId: string): boolean {
        const owner = this.getSessionOwner();
        if (owner && owner.userId !== incomingUserId) {
            return !this.hasToken();
        }
        return false;
    }

    /**
     * Async check used by LoginComponent before allowing submission.
     */
    async verifyLockBeforeLogin(incomingUserId: string): Promise<'LOCKED' | 'PROCEED'> {
        const owner = this.getSessionOwner();
        if (!owner) return 'PROCEED';

        if (owner.userId === incomingUserId) {
            return 'PROCEED';
        }

        return 'LOCKED';
    }

    private getSessionOwner(): SessionOwner | null {
        try {
            const owner = localStorage.getItem(LS_OWNER_KEY);
            return owner ? JSON.parse(owner) : null;
        } catch {
            return null;
        }
    }

    private setSessionOwner(userId: string, signature?: string, companyId?: number, companyName?: string, name?: string, role?: string, roleId?: string): void {
        const owner: SessionOwner = { userId, signature, companyId, companyName, name, role, roleId };
        localStorage.setItem(LS_OWNER_KEY, JSON.stringify(owner));
    }

    public clearSessionOwner(): void {
        localStorage.removeItem(LS_OWNER_KEY);
    }

    // ─── Application Initialization & Validation ─────────────────────────────

    // ─── Disbursement Mode Isolation ────────────────────────────────────────

    /**
     * Returns true if the current tab is in disbursement-only mode.
     * When true, normal session restore and /verify are bypassed.
     */
    public isDisbursementMode(): boolean {
        return sessionStorage.getItem(DISBURSEMENT_FLAG) === 'true';
    }

    /**
     * Enters disbursement mode for the current tab only.
     * Clears sessionStorage (tab-scoped) and raises the isolation flag.
     * Does NOT clear localStorage and does NOT broadcast to other tabs.
     */
    public enterDisbursementMode(): void {
        // Preserve disbursement token before clearing
        const disbursementToken = sessionStorage.getItem('disbursementToken');
        sessionStorage.clear();
        sessionStorage.setItem(DISBURSEMENT_FLAG, 'true');

        // Restore disbursement token
        if (disbursementToken) {
            sessionStorage.setItem('disbursementToken', disbursementToken);
        }

        this.userProfile$.next(null);
        this.sessionValid$.next(false);
        this.checkPromise = null;
        this.pendingInitPromise = null;
    }

    /**
     * Call this when the disbursement flow ends cleanly (e.g. on success or logout).
     */
    public exitDisbursementMode(): void {
        sessionStorage.removeItem(DISBURSEMENT_FLAG);
    }

    /**
     * Centralized Production-Ready Validator.
     */
    async ensureSessionState(): Promise<boolean> {
        if (this.isDisbursementMode()) {
            this.sessionValid$.next(false);
            return false;
        }

        const cached = this.currentState();
        if (cached !== null) return cached;

        if (this.pendingInitPromise) return this.pendingInitPromise;

        this.pendingInitPromise = (async () => {
            try {
                const isValid = await this.checkSession();
                if (isValid) {
                    this.restoreFromLocalStorage();
                    return true;
                }

                this.sessionValid$.next(false);
                return false;
            } finally {
                this.pendingInitPromise = null;
            }
        })();

        return this.pendingInitPromise;
    }

    async initSessionCheck(): Promise<boolean> {
        const isValid = await this.ensureSessionState();
        // Removed auto-redirect logic - let guards handle navigation
        return isValid;
    }

    private initVisibilityListener(): void {
        this.visibilityListener = () => {
            if (document.visibilityState === 'visible') {
                this.validateOnFocus();
            }
        };
        document.addEventListener('visibilitychange', this.visibilityListener);
    }

    private async validateOnFocus(): Promise<void> {
        if (this.isDisbursementMode()) return;

        const initialOwner = this.getSessionOwner();
        if (!initialOwner) return;

        const now = Date.now();
        if ((now - this.lastCheckTime) < 60000) return;

        const isValid = await this.checkSession();

        if (!isValid) {
            console.warn('[AuthService] Focus check: Session is dead. Logging out.');
            this.clearSessionOwner();
            this.handleRemoteLogout();
            return;
        }

        const owner = this.getSessionOwner();
        const currentUsername = sessionStorage.getItem('username');
        const currentUuid = sessionStorage.getItem('uuid');

        const isMatch = owner && (currentUsername === owner.userId || currentUuid === owner.userId);

        if (!isMatch) {
            console.warn(`[AuthService] Focus check: Identity mismatch. Tab=${currentUsername}, Lock=${owner?.userId}. Logging out.`);
            this.handleRemoteLogout();
        }
    }

    // ─── Cross-Tab Synchronization ───────────────────────────────────────────

    private initBroadcastChannel(): void {
        if (typeof BroadcastChannel !== 'undefined') {
            this.authChannel = new BroadcastChannel(CHANNEL_NAME);
            this.authChannel.onmessage = (event) => {
                const type = event.data?.type;
                if (type === 'LOGOUT_ALL') {
                    this.handleRemoteLogout();
                } else if (type === 'LOGOUT_STALE') {
                    this.handleStaleLogout();
                }
            };
        }
    }

    private initStorageListener(): void {
        this.storageListener = (event: StorageEvent) => {
            if (event.key === 'sessionOwner') {
                this.handleStaleLogout();
            }
        };

        window.addEventListener('storage', this.storageListener);
    }

    private handleStaleLogout(): void {
        // Get current user identifier based on session type
        let currentUser: string | null = null;

        currentUser = sessionStorage.getItem('username');
        const owner = this.getSessionOwner();
        if (currentUser && owner && currentUser !== owner.userId) {
            this.handleRemoteLogout();
        }
    }

    public handleRemoteLogout(): void {
        this.clearLocalData();
        this.sessionValid$.next(false);

        // Navigate based on current session type
        const disbursementToken = sessionStorage.getItem('disbursementToken');
        if (disbursementToken) {
            this.router.navigate([`/pda-disburesement/${disbursementToken}`]);
        } else {
            this.router.navigate(['/login']);
        }

    }

    public manualLogout(): void {
        // Clear all browser data (localStorage + sessionStorage)
        this.clearBrowserSession();
        this.sessionValid$.next(false);

        // Notify other tabs about manual logout
        this.authChannel?.postMessage({ type: 'LOGOUT_ALL' });

        // Navigate to login
        this.router.navigate(['/login']);
    }

    broadcastLogout(mode: 'ALL' | 'STALE' = 'ALL'): void {
        if (mode === 'ALL') {
            this.clearLocalData();
            this.sessionValid$.next(false);
            this.router.navigate(['/login']);
        }
        this.authChannel?.postMessage({ type: mode === 'ALL' ? 'LOGOUT_ALL' : 'LOGOUT_STALE' });
    }
    public clearBrowserSession(): void {
        this.clearLocalData();
        this.clearSessionOwner();
        const keys = ['name', 'role', 'roleId', 'uuid',
            'username', 'companyId', 'companyName', 'signature'
        ];

        keys.forEach(key => localStorage.removeItem(key));

        // Clear disbursement mode flag
        this.exitDisbursementMode();

        this.sessionValid$.next(false);
    }

    private clearLocalData(): void {
        const keys = ['name', 'role', 'roleId', 'uuid', 'username',
            'companyId', 'companyName', 'signature'];

        keys.forEach(key => sessionStorage.removeItem(key));

        // Clear disbursement-specific data but preserve token for redirect
        const isDisbursement = this.isDisbursementMode();
        const disbursementToken = sessionStorage.getItem('disbursementToken');

        sessionStorage.removeItem('disbursementUuid');
        sessionStorage.removeItem('pa-success-response');
        sessionStorage.removeItem('addressAndBankDetails');

        // Clear disbursement mode flag
        this.exitDisbursementMode();

        // Restore token if this was a disbursement session
        if (isDisbursement && disbursementToken) {
            sessionStorage.setItem('disbursementToken', disbursementToken);
        }

        this.checkPromise = null;
    }

    // ─── Session Validation ──────────────────────────────────────────────────

    checkSession(): Promise<boolean> {
        // Disbursement flow: never call /verify — return false immediately
        if (this.isDisbursementMode()) {
            return Promise.resolve(false);
        }

        if (this.checkPromise !== null) return this.checkPromise;

        const url = `${environment.apiBaseDemUrl}/verify`;
        this.checkPromise = firstValueFrom(
            this.http.get<any>(url, { withCredentials: true })
        ).then((user) => {
            this.lastCheckTime = Date.now();

            const isValid = user?.valid === 'success' || !!user?.uuid || !!user?.userId;
            const uid = user?.username || user?.uuid || user?.userId;

            if (isValid && uid) {
                const currentUsername = sessionStorage.getItem('username');
                const currentUuid = sessionStorage.getItem('uuid');
                const verifiedUsername = user?.username;
                const verifiedUuid = user?.uuid || user?.userId;

                const identityMismatch =
                    (currentUsername && verifiedUsername && currentUsername !== verifiedUsername) ||
                    (currentUuid && verifiedUuid && currentUuid !== verifiedUuid.toString());

                if (identityMismatch) {
                    console.warn(
                        `[AuthService] Session switched. Tab user=${currentUsername || currentUuid}, Verified user=${verifiedUsername || verifiedUuid}. Logging out stale tab.`
                    );
                    this.handleRemoteLogout();
                    return false;
                }

                this.populateSessionStorage(user);

                // Preserve the lock with cached fields
                const name = sessionStorage.getItem('name') || '';
                const role = sessionStorage.getItem('role') || '';
                const sig = sessionStorage.getItem('signature') || '';
                const cId = Number(sessionStorage.getItem('companyId'));
                const cName = sessionStorage.getItem('companyName') || '';
                const roleId = sessionStorage.getItem('roleId') || '';

                this.setSessionOwner(uid, sig, cId, cName, name, role, roleId);

                this.sessionValid$.next(true);
                return true;
            }

            console.warn('[AuthService] Server response invalid or unauthorized. Session invalid.');
            this.sessionValid$.next(false);
            return false;
        }).catch((err) => {
            this.lastCheckTime = Date.now();
            this.sessionValid$.next(false);
            return false;
        }).finally(() => {
            this.checkPromise = null;
        });

        return this.checkPromise;
    }

    setSession(userId: string, user: any): void {
        this.populateSessionStorage(user);

        const keys = [
            'name', 'role', 'roleId', 'uuid', 'username',
            'companyId', 'companyName', 'signature'
        ];

        keys.forEach(key => {
            const val = sessionStorage.getItem(key);
            if (val) {
                localStorage.setItem(key, val);
            }
        });

        const sig = user.email_signature || '';
        const cId = user.company_id || '';
        const cName = user.company_name || '';
        const name = user.name || '';
        const role = user.role_name || '';
        const roleId = user.role_id || '';

        this.setSessionOwner(userId, sig, cId, cName, name, role, roleId);
        this.sessionValid$.next(true);
        this.lastCheckTime = Date.now();
    }

    private populateSessionStorage(user: any): void {

        const isNumeric = (val: any) => /^\d+$/.test(String(val));
        const currentUsername = sessionStorage.getItem('username');
        const currentUuid = sessionStorage.getItem('uuid');
        const owner = this.getSessionOwner();

        if (user.name) sessionStorage.setItem('name', user.name);
        if (user.role_name) sessionStorage.setItem('role', user.role_name);
        if (user.role_id) sessionStorage.setItem('roleId', user.role_id.toString());

        // Identity protection: Don't let a numeric ID (like '10') overwrite a rich username (like 'surjit')
        const newUuid = user.uuid || user.userId;
        if (newUuid && !(currentUuid && !isNumeric(currentUuid) && isNumeric(newUuid))) {
            sessionStorage.setItem('uuid', newUuid.toString());
        } else if (newUuid) {
            sessionStorage.setItem('uuid', newUuid.toString());
        }

        const newUsername = user.username || user.userId;
        if (newUsername && !(currentUsername && !isNumeric(currentUsername) && isNumeric(newUsername))) {
            sessionStorage.setItem('username', newUsername.toString());
        } else if (newUsername) {
            sessionStorage.setItem('username', newUsername.toString());
        }

        // BACK-FILL logic for missing fields (Server response beats Cache, Cache beats empty)
        const companyId = user.company_id || owner?.companyId;
        if (companyId) sessionStorage.setItem('companyId', companyId.toString());

        const companyName = user.company_name || owner?.companyName;
        if (companyName) sessionStorage.setItem('companyName', companyName);

        const signature = (user.email_signature !== undefined && user.email_signature !== null)
            ? user.email_signature
            : owner?.signature;
        if (signature !== undefined && signature !== null) {
            sessionStorage.setItem('signature', signature);
        }

        // Push to reactive stream for UI to update instantly
        this.refreshProfileFromStorage();
    }

    currentState(): boolean | null {
        return this.sessionValid$.getValue();
    }

    private restoreFromLocalStorage(): void {
        if (this.isDisbursementMode()) return;

        const sessionUser = sessionStorage.getItem('username');
        const localUser = localStorage.getItem('username');

        if (sessionUser && localUser && sessionUser !== localUser) {
            console.warn('[AuthService] User mismatch (session vs local) → clearing session and redirecting to /login');
            this.userProfile$.next(null);
            this.sessionValid$.next(false);
            this.router.navigate(['/login']);
            return;
        }

        const username = sessionStorage.getItem('username');
        const role = sessionStorage.getItem('role');

        if (!username || !role) {
            const keys = [
                'name', 'role',
                'companyId', 'companyName', 'signature'
            ];

            let hasData = false;

            keys.forEach(key => {
                const val = localStorage.getItem(key);
                if (val) {
                    sessionStorage.setItem(key, val);
                    hasData = true;
                }
            });

            if (hasData) {
                this.refreshProfileFromStorage();
            }
        }
    }
    public setDisbursementOwner(userId: string): void {
        this.setSessionOwner(userId);
    }

    ngOnDestroy(): void {
        this.authChannel?.close();
        if (this.visibilityListener) {
            document.removeEventListener('visibilitychange', this.visibilityListener);
        }

        if (this.storageListener) {
            window.removeEventListener('storage', this.storageListener);
        }
    }
}
